export type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function getString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function isArrayLike(value: unknown): value is ArrayLike<unknown> {
  return Boolean(value) && typeof (value as { length?: unknown }).length === 'number';
}

export interface SeatView {
  seat: number;
  name: string;
  connected: boolean;
  ready: boolean;
  hasOwner: boolean;
  ownerSessionId?: string;
}

export interface RoomStateView {
  seats: SeatView[];
  difficulty: string | null;
  mode: string | null;
  phase: string | null;
  totalPlayers: number | null;
  hostSessionId: string | null;
}

export function toSeatView(seat: number, value: unknown): SeatView {
  const record = isRecord(value) ? value : null;
  const ownerSessionId = getString(record?.ownerSessionId);
  return {
    seat,
    name: getString(record?.name) ?? '',
    connected: Boolean(record?.connected),
    ready: Boolean(record?.ready),
    hasOwner: Boolean(ownerSessionId),
    ownerSessionId: ownerSessionId ?? undefined,
  };
}

function toUnknownArray(value: ArrayLike<unknown>): unknown[] {
  const length = Math.max(0, Math.min(64, value.length));
  return Array.from({ length }, (_, i) => (value as unknown as { [k: number]: unknown })[i]);
}

export function parseRoomStateView(state: unknown): RoomStateView {
  const record = isRecord(state) ? state : null;
  const seatsValue = record?.seats;

  const seatsRaw = isArrayLike(seatsValue) ? toUnknownArray(seatsValue) : [];
  const seats = seatsRaw.map((value, index) => toSeatView(index, value));

  return {
    seats,
    difficulty: getString(record?.difficulty),
    mode: getString(record?.mode),
    phase: getString(record?.phase),
    totalPlayers: typeof record?.totalPlayers === 'number' ? record.totalPlayers : null,
    hostSessionId: getString(record?.hostSessionId),
  };
}
